import { Component, Inject, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Observable, of } from 'rxjs';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { ProviderSearchFilters } from '../../../../shared/model/providerlocationsearch/GetProviderSearchFilterResponse';
import { PostGetProviderSearchRequest } from '../../../../shared/model/providerlocationsearch/PostGetProviderSearchRequest';
import { LocationCoordinates } from '../../../../shared/model/LocationCoordinates';
import { SafeMethod } from '../../../../shared/decorators/safe-method.decorator';
import { TranslateService } from '@ngx-translate/core';

interface BottomSheetData {
  queryCityOrZip: string;
  distanceSliderValue: number;
  currentFilters: ProviderSearchFilters | null;
  queryNameOrSpecialty: string;
  currentLocation: LocationCoordinates | null;
  providerSortMethod: 'distance' | 'availability' | 'name';
  allResults: any[];
  cityZipTypeAheadOptions: string[];
}

@Component({
  selector: 'app-distance-filter-bottom-sheet',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatInputModule, MatFormFieldModule, MatAutocompleteModule],
  templateUrl: './distance-filter-bottom-sheet.component.html',
  styleUrl: './distance-filter-bottom-sheet.component.css'
})
export class DistanceFilterBottomSheetComponent {
  searchState = inject(SearchStateService);
  mcAppGateway = inject(MCAppGatewayService);

  queryCityOrZip: string = '';
  distanceSliderValue: number = 2;
  currentFilters: ProviderSearchFilters | null = null;
  queryNameOrSpecialty: string = '';
  currentLocation: LocationCoordinates | null = null;
  providerSortMethod: 'distance' | 'availability' | 'name' = 'distance';
  allResults: any[] = [];
  cityZipTypeAheadOptions: string[] = [];

  filteredCityZipOptions$: Observable<string[]> = of([]);

  locale = "en-US";
  locationText = "Location";
  clearBtnText = "Clear";
  distanceText = "Distance (miles)";
  applyBtnText = "Apply"
  locationPlaceholderText = "Enter city or ZIP";


  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: BottomSheetData,
    private bottomSheetRef: MatBottomSheetRef<DistanceFilterBottomSheetComponent>,
    private translate: TranslateService,) {

    this.locale =  sessionStorage.getItem('locale') || 'en-US';
    this.translate.use(this.locale); // Default to 'en-US' if no locale is provided
    
    // Initialize component state from passed data
    this.queryCityOrZip = data.queryCityOrZip;
    this.distanceSliderValue = data.distanceSliderValue;
    this.currentFilters = data.currentFilters;
    this.queryNameOrSpecialty = data.queryNameOrSpecialty;
    this.currentLocation = data.currentLocation;
    this.providerSortMethod = data.providerSortMethod;
    this.allResults = data.allResults;
    this.cityZipTypeAheadOptions = data.cityZipTypeAheadOptions;
  }

  ngOnInit() {
    // Initialize autocomplete with all options
    this.filteredCityZipOptions$ = of(this.cityZipTypeAheadOptions);

    // Load translations
    this.translate.get('LOCATION').subscribe((translated: string) => {
      this.locationText = translated;
    });

    this.translate.get('CLEAR').subscribe((translated: string) => {
      this.clearBtnText = translated;
    });

    this.translate.get('DISTANCE_MILES').subscribe((translated: string) => {
      this.distanceText = translated;
    });

    this.translate.get('APPLY').subscribe((translated: string) => {
      this.applyBtnText = translated;
    });

    this.translate.get('PROVIDER_CITYZIP_PLACEHOLDER').subscribe((translated: string) => {
      this.locationPlaceholderText = translated;
    });

    // Initialize autocomplete with all options
    this.filteredCityZipOptions$ = of([]);
  }

  filterCityZipOptions(searchText: string): void {
    if (!searchText || searchText.trim().length < 3) {
      this.filteredCityZipOptions$ = of([]);
    } else {
      // Remove spaces, special charaters, and convert to lowercase for comparison
      const normalizedFilter = searchText.toLowerCase().replace(/\s/g, '');
      const filtered = this.cityZipTypeAheadOptions.filter(option =>
        option.toLowerCase().replace(/\s/g, '').replace(/[^a-z0-9]/g, '').includes(normalizedFilter)
      );
      this.filteredCityZipOptions$ = of(filtered);
    }
  }

  @SafeMethod()
  close(): void {
    this.bottomSheetRef.dismiss();
  }

  @SafeMethod(15)
  getDistanceInMilesFromSliderValue(sliderValue: number): number {
    switch (sliderValue) {
      case 0:
        return 5;
      case 1:
        return 10;
      case 2:
        return 15;
      case 3:
        return 25;
      case 4:
        return 50;
      default:
        return 15; // Default to 15 miles
    }
  }

  @SafeMethod()
  onClearLocationFilter(): void {
    this.queryCityOrZip = this.currentFilters?.location || this.data.queryCityOrZip;
    
    if (this.currentFilters && this.currentFilters.distanceInMiles && this.currentFilters.distanceInMiles > 0) {
      this.setDistanceFilterValue(this.currentFilters.distanceInMiles);
    }
  }

  @SafeMethod()
  setDistanceFilterValue(distanceInMiles: number): void {
    // Map distance in miles to slider value
    switch (distanceInMiles) {
      case 5:
        this.distanceSliderValue = 0;
        break;
      case 10:
        this.distanceSliderValue = 1;
        break;
      case 15:
        this.distanceSliderValue = 2;
        break;
      case 25:
        this.distanceSliderValue = 3;
        break;
      case 50:
        this.distanceSliderValue = 4;
        break;
      default:
        this.distanceSliderValue = 2; // Default to 15 miles
    }
  }

  @SafeMethod()
  onApplyLocationFilter(): void {
    if (this.currentFilters) {
      var request: PostGetProviderSearchRequest = new PostGetProviderSearchRequest();

      request.providerName = this.currentFilters.providerName;

      if ((request.providerName == null || request.providerName == '') && this.queryNameOrSpecialty != '') {
        request.nameOrSpecialty = this.queryNameOrSpecialty;
      }
      request.cityOrZip = this.queryCityOrZip;
      request.distanceInMiles = this.getDistanceInMilesFromSliderValue(this.distanceSliderValue) ?? 15;

      request.specialties = this.currentFilters?.specialtyList?.filter(s => s.isSelected).map(s => s.value).join(', ');
      request.onlineScheduling = this.currentFilters?.onlineSchedulingList?.some(o => o.isSelected) ? true : undefined;
      request.affiliations = this.currentFilters?.affiliationList?.filter(a => a.isSelected).map(a => a.value).join(', ');
      request.gender = this.currentFilters?.genderList?.find(g => g.isSelected)?.value ?? '';
      request.languages = this.currentFilters?.languageList?.filter(l => l.isSelected).map(l => l.value).join(', ');

      // Only set the latitude and longitude if a cityOrZip value is not provided
      if ((request.cityOrZip == null || request.cityOrZip == "") && this.currentLocation != null && this.currentLocation.latitude && this.currentLocation.longitude) {
        request.latitude = this.currentLocation.latitude;
        request.longitude = this.currentLocation.longitude;
      }

      this.searchState.setShowSpinner(true);
      
      // Call search API
      this.mcAppGateway.PostGetProviderSearch(request).subscribe({
        next: (response) => {
          if (this.currentFilters != null) {
            this.currentFilters.location = request.cityOrZip ?? '';
            this.currentFilters.distanceInMiles = request.distanceInMiles ?? 15;
            this.searchState.setProviderSearchFilters(this.currentFilters);

            this.searchState.setProviderSearchQuery({
              nameOrSpecialty: this.currentFilters.providerName != '' ? this.currentFilters.providerName : this.queryNameOrSpecialty,
              cityOrZip: this.queryCityOrZip,
            });
          }

          var sortedResponse = this.searchState.sortProviderSearchResults(response || [], this.providerSortMethod);
          console.log('Provider Search Response after applying location filter:', sortedResponse);
          this.searchState.setProviderSearchResults(sortedResponse);
          this.searchState.setShowSpinner(false);

          // Close bottom sheet and pass updated data back
          this.bottomSheetRef.dismiss({
            queryCityOrZip: this.queryCityOrZip,
            distanceSliderValue: this.distanceSliderValue
          });
        },
        error: (error) => {
          console.error('Error fetching provider search results after applying location filter:', error);
          alert('An error occurred while fetching provider search results. Please try again later.');
          this.searchState.setShowSpinner(false);
        }
      });
    }
  }
}
